/**
 * Holidays and closures a user types in, expanded to the days they cover.
 *
 * Nothing is hard-coded: a calendar marks only what the reader adds. One entry per line:
 *
 *   2026-12-25 Winter holiday          one day
 *   2026-12-21..2027-01-01 Winter break   a range (also "to" or an en dash)
 *   12-25 Christmas Day                every year
 *   12-24..01-02 Winter break          a range that repeats every year
 */

import {
  addDays,
  type CivilDate,
  dayNumber,
  daysInMonth,
  isoString,
  MAX_YEAR,
  MIN_YEAR,
  parseIsoDate,
} from "@/lib/tool-kit/printables/calendar";

export interface FixedHoliday {
  kind: "fixed";
  start: CivilDate;
  end: CivilDate;
  label: string;
}

export interface YearlyHoliday {
  kind: "yearly";
  startMonth: number;
  startDay: number;
  endMonth: number;
  endDay: number;
  label: string;
}

export type Holiday = FixedHoliday | YearlyHoliday;

export interface HolidayParse {
  holidays: Holiday[];
  /** Lines that could not be read, as typed. */
  errors: string[];
}

export const MAX_HOLIDAYS = 200;
export const MAX_RANGE_DAYS = 366;

const WHITESPACE = /\s+/;
const DASH_RANGE = /[–—]/;
const MONTH_DAY = /^(\d{1,2})-(\d{1,2})$/;

interface Part {
  full: CivilDate | null;
  monthDay: { month: number; day: number } | null;
}

function parsePart(text: string): Part {
  const full = parseIsoDate(text);
  if (full) {
    return { full, monthDay: null };
  }
  const match = MONTH_DAY.exec(text.trim());
  if (match) {
    const month = Number(match[1]) - 1;
    const day = Number(match[2]);
    // 29 February is allowed: it simply does not appear in a non-leap year.
    if (
      month >= 0 &&
      month <= 11 &&
      day >= 1 &&
      day <= daysInMonth(2024, month)
    ) {
      return { full: null, monthDay: { month, day } };
    }
  }
  return { full: null, monthDay: null };
}

function fixedFrom(a: Part, b: Part, label: string): FixedHoliday | null {
  if (!(a.full && b.full)) {
    return null;
  }
  const length =
    dayNumber(b.full.year, b.full.month, b.full.day) -
    dayNumber(a.full.year, a.full.month, a.full.day);
  if (length < 0 || length >= MAX_RANGE_DAYS) {
    return null;
  }
  return { kind: "fixed", start: a.full, end: b.full, label };
}

function yearlyFrom(a: Part, b: Part, label: string): YearlyHoliday | null {
  if (!(a.monthDay && b.monthDay)) {
    return null;
  }
  return {
    kind: "yearly",
    startMonth: a.monthDay.month,
    startDay: a.monthDay.day,
    endMonth: b.monthDay.month,
    endDay: b.monthDay.day,
    label,
  };
}

interface Entry {
  first: string;
  second: string | undefined;
  label: string;
}

const RANGE_WORDS = new Set(["to", "–", "—"]);

/** Splits a line into its first date, an optional second date, and the label. */
function splitEntry(line: string): Entry {
  const [head = "", ...rest] = line.split(WHITESPACE);
  const dotted = head.split("..");
  if (dotted.length === 2) {
    return { first: dotted[0] ?? "", second: dotted[1], label: rest.join(" ") };
  }
  const dashed = head.split(DASH_RANGE);
  if (dashed.length === 2) {
    return { first: dashed[0] ?? "", second: dashed[1], label: rest.join(" ") };
  }
  const [word = "", end, ...label] = rest;
  if (RANGE_WORDS.has(word.toLowerCase()) && end !== undefined) {
    return { first: head, second: end, label: label.join(" ") };
  }
  return { first: head, second: undefined, label: rest.join(" ") };
}

/** Reads the holiday list; unreadable lines are returned, not guessed at. */
export function parseHolidays(raw: string): HolidayParse {
  const holidays: Holiday[] = [];
  const errors: string[] = [];
  for (const line of raw.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const { first, second, label } = splitEntry(trimmed);
    const a = parsePart(first);
    const b = second === undefined ? a : parsePart(second);
    const parsed =
      fixedFrom(a, b, label.trim()) ?? yearlyFrom(a, b, label.trim());
    if (parsed) {
      holidays.push(parsed);
    } else {
      errors.push(trimmed);
    }
  }
  return { holidays: holidays.slice(0, MAX_HOLIDAYS), errors };
}

/** Adds one covered day to the map. */
function mark(
  map: Map<string, string[]>,
  date: CivilDate,
  label: string
): void {
  if (date.year < MIN_YEAR || date.year > MAX_YEAR) {
    return;
  }
  const key = isoString(date);
  const labels = map.get(key) ?? [];
  if (label && !labels.includes(label)) {
    labels.push(label);
  }
  map.set(key, labels);
}

function expandRange(
  map: Map<string, string[]>,
  start: CivilDate,
  end: CivilDate,
  label: string
) {
  const length =
    dayNumber(end.year, end.month, end.day) -
    dayNumber(start.year, start.month, start.day);
  for (let i = 0; i <= Math.min(length, MAX_RANGE_DAYS); i += 1) {
    mark(map, addDays(start, i), label);
  }
}

/**
 * Every covered day within `years`, keyed `YYYY-MM-DD`, with the labels for that day. A
 * yearly entry that runs past 31 December carries into the next year; a yearly 29
 * February appears only in leap years.
 */
export function holidayMap(
  holidays: readonly Holiday[],
  years: readonly number[]
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const holiday of holidays) {
    if (holiday.kind === "fixed") {
      expandRange(map, holiday.start, holiday.end, holiday.label);
      continue;
    }
    for (const year of years) {
      if (holiday.startDay > daysInMonth(year, holiday.startMonth)) {
        continue;
      }
      const start: CivilDate = {
        year,
        month: holiday.startMonth,
        day: holiday.startDay,
      };
      const wraps =
        holiday.endMonth < holiday.startMonth ||
        (holiday.endMonth === holiday.startMonth &&
          holiday.endDay < holiday.startDay);
      const endYear = wraps ? year + 1 : year;
      const endDay = Math.min(
        holiday.endDay,
        daysInMonth(endYear, holiday.endMonth)
      );
      expandRange(
        map,
        start,
        { year: endYear, month: holiday.endMonth, day: endDay },
        holiday.label
      );
    }
  }
  return map;
}

/** Labels for a date, or an empty list. */
export function labelsFor(
  map: ReadonlyMap<string, string[]>,
  date: CivilDate
): string[] {
  return map.get(isoString(date)) ?? [];
}

export function isHoliday(
  map: ReadonlyMap<string, string[]>,
  date: CivilDate
): boolean {
  return map.has(isoString(date));
}

export interface LegendEntry {
  start: CivilDate;
  end: CivilDate;
  label: string;
}

/**
 * The marked days in `months` as a short list: consecutive days with the same label
 * become one range. Days with no label are listed only if no other label covers them.
 */
export function legendEntries(
  map: ReadonlyMap<string, string[]>,
  months: readonly { year: number; month: number }[]
): LegendEntry[] {
  const entries: LegendEntry[] = [];
  let open: LegendEntry | null = null;
  let previous = Number.NaN;
  for (const { year, month } of months) {
    for (let day = 1; day <= daysInMonth(year, month); day += 1) {
      const date: CivilDate = { year, month, day };
      const labels = labelsFor(map, date).filter((label) => label.length > 0);
      const number = dayNumber(year, month, day);
      if (labels.length === 0) {
        open = null;
        previous = number;
        continue;
      }
      const label = labels.join("; ");
      if (open && open.label === label && number === previous + 1) {
        open.end = date;
      } else {
        open = { start: date, end: date, label };
        entries.push(open);
      }
      previous = number;
    }
  }
  return entries;
}
